import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Bell, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListNotifications,
  useMarkNotificationsRead,
  getListNotificationsQueryKey,
  type Notification as ApiNotification,
} from "@workspace/api-client-react";
import { cn } from "@/lib/utils";

const POLL_MS = 30_000;

/**
 * Bell + badge in the sidebar that shows recent notifications and
 * lets the rep mark them read. Re-fetches every 30 s so a freshly
 * inserted "demo opened" alert (Task #161) shows up without a hard
 * reload, and pops a desktop notification for any unseen rows when
 * the browser permission is granted.
 */
export function NotificationsBell() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // The largest `created_at` we have already shown a desktop toast
  // for. Used to suppress re-toasting on every poll cycle.
  const lastToastedAtRef = useRef<number>(0);
  // Has the very first successful query landed yet? Tracked separately
  // so that an initially-empty inbox still seeds the watermark — without
  // this guard, the very first notification ever delivered to the rep
  // would be silently treated as the seed and skip its desktop toast.
  const hasInitializedRef = useRef<boolean>(false);

  const listParams = { limit: 20 };
  const { data, isLoading } = useListNotifications(listParams, {
    query: {
      queryKey: getListNotificationsQueryKey(listParams),
      refetchInterval: POLL_MS,
      refetchIntervalInBackground: true,
      staleTime: 0,
    },
  });

  const markRead = useMarkNotificationsRead({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListNotificationsQueryKey({ limit: 20 }),
        });
      },
    },
  });

  // Side-effect: pop a desktop notification for any new unread rows
  // since the last poll. The first successful query (whether the
  // inbox was empty or pre-populated) only seeds the watermark — we
  // never re-toast the backlog the rep already had on page load.
  // Subsequent polls toast every newly-arrived unread row.
  useEffect(() => {
    if (!data) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    const items = data.items ?? [];

    if (!hasInitializedRef.current) {
      // Seed from the most recent existing item (or "now" if empty)
      // so we never re-toast pre-existing notifications, but the
      // very next genuinely new arrival still pops.
      lastToastedAtRef.current = items.length
        ? Math.max(...items.map((n) => new Date(n.created_at).getTime()))
        : Date.now();
      hasInitializedRef.current = true;
      return;
    }

    if (items.length === 0) return;

    for (const n of items) {
      const ts = new Date(n.created_at).getTime();
      if (ts > lastToastedAtRef.current && !n.read_at) {
        try {
          const toast = new Notification(n.title, {
            body: n.body ?? "",
            tag: n.id,
          });
          if (n.lead_id) {
            const leadId = n.lead_id;
            toast.onclick = () => {
              window.focus();
              setLocation(`/leads/${leadId}`);
              toast.close();
            };
          }
        } catch {
          /* ignore — Notification can throw inside iframes */
        }
      }
    }
    lastToastedAtRef.current = Math.max(
      lastToastedAtRef.current,
      ...items.map((n) => new Date(n.created_at).getTime()),
    );
  }, [data, setLocation]);

  const unread = data?.unread_count ?? 0;
  const items = data?.items ?? [];

  function handleItemClick(n: ApiNotification) {
    if (!n.read_at) {
      markRead.mutate({ data: { ids: [n.id] } });
    }
    if (n.lead_id) {
      setLocation(`/leads/${n.lead_id}`);
      setOpen(false);
    }
  }

  function handleMarkAll() {
    if (unread === 0) return;
    markRead.mutate({ data: {} });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="button-notifications-bell"
          aria-label={
            unread > 0 ? `${unread} unread notifications` : "Notifications"
          }
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              data-testid="badge-notifications-unread"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0"
        data-testid="popover-notifications"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <p className="text-sm font-medium">Notifications</p>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={handleMarkAll}
            disabled={unread === 0 || markRead.isPending}
            data-testid="button-notifications-mark-all"
          >
            {markRead.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Check className="w-3 h-3 mr-1" />
            )}
            Mark all read
          </Button>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {isLoading && items.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              No notifications yet. We&apos;ll ping you the moment a prospect
              opens a demo you texted them.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(n)}
                    data-testid={`notification-${n.id}`}
                    className={cn(
                      "w-full text-left px-3 py-2.5 hover:bg-muted/40 transition-colors",
                      !n.read_at && "bg-sky-500/5",
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read_at && (
                        <span className="mt-1.5 block w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-tight">
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {n.body}
                          </p>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatRelative(n.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - then);
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
